import { useMemo, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { Pill, RequestDialog } from '@/components/csp/CspKit';
import { useCspMe, useCspProducts, useRefreshCspProducts, type CspProduct } from '@/api/csp';
import { money } from '@/lib/csp';
import { cn } from '@/lib/utils';

/**
 * CSP → Products. Microsoft NCE services in Autotask (Services named "MSNCE-…", prices ex GST),
 * with Synnex CSP pricing alongside once that API exists. Request / purchase per product.
 */
export default function CspProductsPage() {
  const products = useCspProducts();
  const refresh = useRefreshCspProducts();
  const me = useCspMe();
  const [q, setQ] = useState('');
  const [buying, setBuying] = useState<CspProduct | null>(null);

  const list = products.data?.products ?? [];
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((p) => !s || p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s) || (p.productCode ?? '').toLowerCase().includes(s));
  }, [list, q]);
  const synnex = !!products.data?.synnexConnected;

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
      <PageHeader title="Products" count={products.data ? rows.length : null} isFetching={products.isFetching || refresh.isPending}
        subtitle="Microsoft NCE services in Autotask (MSNCE-…), cross-referenced against Synnex CSP pricing"
        actions={<>
          <SearchInput withIcon placeholder="Product, SKU or code" className="w-64" value={q} onValueChange={setQ} />
          <Button variant="ghost" size="sm" onClick={() => refresh.mutate()} disabled={refresh.isPending} title="Re-read services from Autotask">
            <RefreshCw className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')} />
          </Button>
        </>}
      />

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="MSNCE services" value={list.length} />
        <StatCard label="Monthly commitment" value={list.filter((p) => p.term === 'Monthly').length} />
        <StatCard label="Yearly commitment" value={list.filter((p) => p.term === 'Yearly').length} />
        <div className="rounded-lg border border-border bg-card p-4 text-sm shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Sources</div>
          <Source ok={!products.error && !!products.data} label="Autotask" />
          <Source ok={synnex} label={synnex ? 'Synnex' : 'Synnex — not connected'} />
        </div>
      </div>

      {products.isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading services from Autotask…</div>
      ) : products.error ? (
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load Autotask services: {products.error instanceof Error ? products.error.message : 'unknown error'}
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 font-medium">SKU / term</th>
                <th className="px-4 py-2 text-right font-medium">AT cost</th>
                <th className="px-4 py-2 text-right font-medium">AT sell</th>
                <th className="px-4 py-2 text-right font-medium">Margin</th>
                <th className="px-4 py-2 text-right font-medium">Synnex cost</th>
                <th className="px-4 py-2 text-right font-medium">Synnex sell</th>
                <th className="w-[1%] px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No matching products.</td></tr>}
              {rows.map((p) => (
                <tr key={p.atServiceId} className="hover:bg-muted/30">
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{p.name}</div>
                    {p.productCode && <div className="text-[11px] text-muted-foreground">{p.productCode}</div>}
                  </td>
                  <td className="px-4 py-2">
                    <code className="text-xs">{p.sku}</code>
                    <div className="mt-1 flex items-center gap-1.5">
                      {p.term && <Pill tone={p.term === 'Yearly' ? 'good' : 'warn'}>{p.term}</Pill>}
                      {p.billing && <span className="text-[11px] text-muted-foreground">billed {p.billing.toLowerCase()}</span>}
                    </div>
                  </td>
                  <PriceCell value={p.atCost} compare={p.synnexCost} />
                  <PriceCell value={p.atSell} compare={p.synnexSell} />
                  <td className="px-4 py-2 text-right tabular-nums">{money(p.margin)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{money(p.synnexCost)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{money(p.synnexSell)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" className="h-7 text-xs" onClick={() => setBuying(p)}>{me.data?.isApprover ? 'Purchase' : 'Request'}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">
        Prices are ex GST. Once Synnex is connected, an Autotask price <span className="font-medium text-destructive">below</span> Synnex shows red
        (under-priced or under cost) and at/above shows green.
      </p>

      <RequestDialog open={!!buying} onOpenChange={(o) => { if (!o) setBuying(null); }} product={buying} />
    </div>
  );
}

/** Red when the Autotask price is below Synnex, green at/above; plain until Synnex is connected. */
function PriceCell({ value, compare }: { value: number | null; compare: number | null }) {
  const tone = value == null || compare == null ? '' : value < compare ? 'text-destructive font-medium' : 'text-success';
  return <td className={cn('px-4 py-2 text-right tabular-nums', tone)}>{money(value)}</td>;
}

function Source({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground">
      <span className={cn('h-2 w-2 rounded-full', ok ? 'bg-success' : 'bg-destructive')} />{label}
    </div>
  );
}
